import type { ShippingOptions, LabelFormat, LabelSize, DeliveryConfirmation } from '../../types';
import {
  LABEL_FORMAT_OPTIONS,
  LABEL_SIZE_OPTIONS,
  DELIVERY_CONFIRMATION_OPTIONS,
  PRINT_CUSTOM_CODES,
} from '../../constants/settingsOptions';

interface ShippingOptionsSectionProps {
  options: ShippingOptions;
  onChange: <K extends keyof ShippingOptions>(field: K, value: ShippingOptions[K]) => void;
}

function ShippingOptionsSection({ options, onChange }: ShippingOptionsSectionProps) {
  const availableSizes = LABEL_SIZE_OPTIONS.filter((opt) => opt.formats.includes(options.label_format));

  return (
    <div className="form-section">
      <h2>Shipping Options</h2>
      <p className="section-description">
        Configure default shipping preferences. These options align with the EasyPost API.
      </p>

      {/* Label Settings */}
      <h3 className="settings-subsection-title">Label Settings</h3>
      <div className="form-grid-settings">
        <div className="form-group">
          <label htmlFor="label-format">Label Format</label>
          <select
            id="label-format"
            value={options.label_format}
            onChange={(e) => onChange('label_format', e.target.value as LabelFormat)}
          >
            {LABEL_FORMAT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label} - {opt.description}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="label-size">Label Size</label>
          <select
            id="label-size"
            value={options.label_size}
            onChange={(e) => onChange('label_size', e.target.value as LabelSize)}
          >
            {availableSizes.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <p className="field-hint">Available sizes depend on selected format.</p>
        </div>
      </div>

      {/* Delivery Options */}
      <h3 className="settings-subsection-title">Delivery Options</h3>
      <div className="form-grid-settings">
        <div className="form-group full-width">
          <label htmlFor="delivery-confirmation">Delivery Confirmation</label>
          <select
            id="delivery-confirmation"
            value={options.delivery_confirmation || ''}
            onChange={(e) =>
              onChange(
                'delivery_confirmation',
                e.target.value ? (e.target.value as DeliveryConfirmation) : null
              )
            }
          >
            {DELIVERY_CONFIRMATION_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label} - {opt.description}
              </option>
            ))}
          </select>
          <p className="field-hint">
            Some options are carrier-specific. SERVICE_DEFAULT is recommended for international shipments.
          </p>
        </div>

        <div className="form-group full-width">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={options.saturday_delivery}
              onChange={(e) => onChange('saturday_delivery', e.target.checked)}
            />
            Enable Saturday Delivery
          </label>
          <p className="field-hint">Request Saturday delivery when available (may incur additional charges).</p>
        </div>

        <div className="form-group full-width">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={options.hold_for_pickup}
              onChange={(e) => onChange('hold_for_pickup', e.target.checked)}
            />
            Hold for Pickup
          </label>
          <p className="field-hint">Hold package at carrier facility for recipient pickup.</p>
        </div>

        <div className="form-group full-width">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={options.carbon_neutral}
              onChange={(e) => onChange('carbon_neutral', e.target.checked)}
            />
            Carbon Neutral Shipping
          </label>
          <p className="field-hint">Offset carbon emissions for shipments (where supported by carrier).</p>
        </div>
      </div>

      {/* Insurance */}
      <h3 className="settings-subsection-title">Insurance</h3>
      <div className="form-grid-settings">
        <div className="form-group full-width">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={options.insurance_enabled}
              onChange={(e) => onChange('insurance_enabled', e.target.checked)}
            />
            Enable Shipping Insurance
          </label>
          <p className="field-hint">
            EasyPost insurance costs 1% of shipment value. Covers damage, loss, and theft.
          </p>
        </div>

        {options.insurance_enabled && (
          <div className="form-group">
            <label htmlFor="insurance-amount">Default Insurance Amount (USD)</label>
            <input
              type="number"
              id="insurance-amount"
              value={options.default_insurance_amount}
              onChange={(e) => onChange('default_insurance_amount', parseFloat(e.target.value) || 0)}
              placeholder="0.00"
              min="0"
              step="0.01"
            />
            <p className="field-hint">Default value to insure shipments for (in USD).</p>
          </div>
        )}
      </div>

      {/* Custom Print Fields */}
      <h3 className="settings-subsection-title">Custom Label Fields</h3>
      <p className="field-hint" style={{ marginBottom: '16px' }}>
        Add reference numbers, PO numbers, or other custom text to shipping labels.
      </p>
      <div className="form-grid-settings">
        <div className="form-group">
          <label htmlFor="print-custom-1">Custom Field 1</label>
          <input
            type="text"
            id="print-custom-1"
            value={options.print_custom_1}
            onChange={(e) => onChange('print_custom_1', e.target.value)}
            placeholder="e.g., PO-12345"
            maxLength={50}
          />
        </div>

        <div className="form-group">
          <label htmlFor="print-custom-1-code">Field 1 Type</label>
          <select
            id="print-custom-1-code"
            value={options.print_custom_1_code}
            onChange={(e) => onChange('print_custom_1_code', e.target.value)}
          >
            {PRINT_CUSTOM_CODES.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="print-custom-2">Custom Field 2</label>
          <input
            type="text"
            id="print-custom-2"
            value={options.print_custom_2}
            onChange={(e) => onChange('print_custom_2', e.target.value)}
            placeholder="e.g., Order #789"
            maxLength={50}
          />
        </div>

        <div className="form-group">
          <label htmlFor="print-custom-2-code">Field 2 Type</label>
          <select
            id="print-custom-2-code"
            value={options.print_custom_2_code}
            onChange={(e) => onChange('print_custom_2_code', e.target.value)}
          >
            {PRINT_CUSTOM_CODES.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}

export default ShippingOptionsSection;
